import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
  currentWeather: null,
  forecast: [],
  loading: false,
  error: null,
  notification: null,
  selectedZoneId: null
};

// Action types
const WEATHER_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  SET_SELECTED_ZONE: 'SET_SELECTED_ZONE'
};

// Reducer function
const weatherReducer = (state, action) => {
  switch (action.type) {
    case WEATHER_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case WEATHER_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        currentWeather: action.payload.currentWeather,
        forecast: action.payload.forecast,
        error: null
      };
    case WEATHER_ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case WEATHER_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: {
          type: action.payload.type,
          message: action.payload.message
        }
      };
    case WEATHER_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null
      };
    case WEATHER_ACTIONS.SET_SELECTED_ZONE:
      return {
        ...state,
        selectedZoneId: action.payload
      };
    default:
      return state;
  }
};

// Create context
const WeatherContext = createContext();

/**
 * Weather Context Provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const WeatherContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(weatherReducer, initialState);

  // Set notification
  const setNotification = useCallback((type, message, duration = 5000) => {
    dispatch({
      type: WEATHER_ACTIONS.SET_NOTIFICATION,
      payload: { type, message }
    });

    // Auto-clear notification after duration
    setTimeout(() => {
      dispatch({ type: WEATHER_ACTIONS.CLEAR_NOTIFICATION });
    }, duration);
  }, []);

  // Clear notification
  const clearNotification = useCallback(() => {
    dispatch({ type: WEATHER_ACTIONS.CLEAR_NOTIFICATION });
  }, []);

  // Set selected zone
  const setSelectedZone = useCallback((zoneId) => {
    dispatch({
      type: WEATHER_ACTIONS.SET_SELECTED_ZONE,
      payload: zoneId
    });
  }, []);

  // Load weather data for a specific zone or general farm location
  const loadWeatherData = useCallback(async (zoneId = null) => {
    dispatch({ type: WEATHER_ACTIONS.FETCH_START });
    
    try {
      // In a real app, this would call the API with the zone ID if provided
      // For now, we'll use mock data
      
      // Mock API call
      // const response = await api.get(`/weather${zoneId ? `?zoneId=${zoneId}` : ''}`);
      // const { currentWeather, forecast } = response.data;
      
      // Mock data for development
      const currentWeather = {
        temperature: 22.5,
        condition: 'Partly Cloudy',
        humidity: 65,
        windSpeed: 12,
        windDirection: 'NW',
        precipitation: 0
      };
      
      const forecast = [
        {
          date: new Date().toISOString(),
          high: 24,
          low: 18,
          condition: 'Sunny',
          precipitation: 0
        },
        {
          date: new Date(Date.now() + 86400000).toISOString(), // Tomorrow
          high: 23,
          low: 17,
          condition: 'Partly Cloudy',
          precipitation: 10
        },
        {
          date: new Date(Date.now() + 172800000).toISOString(), // Day after tomorrow
          high: 21,
          low: 16,
          condition: 'Rain',
          precipitation: 70
        },
        {
          date: new Date(Date.now() + 259200000).toISOString(), // 3 days from now
          high: 20,
          low: 15,
          condition: 'Rain',
          precipitation: 60
        },
        {
          date: new Date(Date.now() + 345600000).toISOString(), // 4 days from now
          high: 22,
          low: 16,
          condition: 'Partly Cloudy',
          precipitation: 20
        }
      ];
      
      dispatch({
        type: WEATHER_ACTIONS.FETCH_SUCCESS,
        payload: { currentWeather, forecast }
      });
      
      if (zoneId) {
        setSelectedZone(zoneId);
      }
      
      return { currentWeather, forecast };
    } catch (error) {
      console.error('Error loading weather data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load weather data';
      
      dispatch({
        type: WEATHER_ACTIONS.FETCH_ERROR,
        payload: errorMessage
      });
      
      setNotification('error', errorMessage);
      return null;
    }
  }, [setNotification, setSelectedZone]);

  // Context value
  const contextValue = {
    state,
    loadWeatherData,
    setSelectedZone,
    setNotification,
    clearNotification
  };

  return (
    <WeatherContext.Provider value={contextValue}>
      {children}
    </WeatherContext.Provider>
  );
};

// Custom hook for using the weather context
export const useWeather = () => {
  const context = useContext(WeatherContext);
  
  if (!context) {
    throw new Error('useWeather must be used within a WeatherContextProvider');
  }
  
  return context;
};

export default WeatherContext;
